/* My Portfolio — the reference's strategy table with Qorvex's data in it.
 *
 * A row of outlined stat tiles, a search-and-filter toolbar, and one table
 * listing every strategy available to this trader: the ones they own in Forge
 * and the Atlas strategies they subscribe to. Beneath it, the positions view
 * (allocation donut, P&L bars, margin meter) that the terminal already had.
 *
 * "Start" is deliberately not a one-click action here. Deploying a strategy
 * needs an account, a mode and — for live — an approval, and those live on
 * the Forge and Atlas screens with their risk checks. The button opens that
 * screen; it does not skip it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { useStore } from '@/store'
import { Icon } from '@/components/Icons'
import { Portfolio } from '@/panels/Portfolio'
import { Card, ModeDot, ProductPill, Tile } from '@/panels/dashboardBits'
import { fmtDateTime, fmtMoney, fmtSigned } from '@/lib/format'
import { PRODUCTS } from '@/lib/products'
import type { Page } from '@/components/Terminal'
import type { AtlasSubscription, Strategy, TradingMode } from '@/types'

interface Row {
  key: string
  kind: 'forge' | 'atlas'
  mode: TradingMode
  modeTitle: string
  name: string
  code: string
  product: string
  detail: string
  version: string
  status: string
  when: string
  page: Page
}

export function MyPortfolio({ onNavigate }: { onNavigate: (p: Page) => void }) {
  const positions = useStore((s) => s.positions)
  const executions = useStore((s) => s.executions)

  const [subs, setSubs] = useState<AtlasSubscription[] | null>(null)
  const [strategies, setStrategies] = useState<Strategy[] | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<'all' | 'forge' | 'atlas'>('all')
  const [onlyRunning, setOnlyRunning] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  const load = useCallback(async () => {
    const [s, st] = await Promise.allSettled([api.atlasSubscriptions(true), api.strategies()])
    setSubs(s.status === 'fulfilled' ? s.value : [])
    setStrategies(st.status === 'fulfilled' ? (st.value.items ?? []) : [])
    setRefreshedAt(Date.now())
  }, [])
  useEffect(() => { void load() }, [load])

  const rows: Row[] = useMemo(() => {
    const mine: Row[] = (strategies ?? []).map((s) => ({
      key: `f-${s.id}`, kind: 'forge',
      mode: s.approved_for_live ? 'LIVE' : 'PAPER',
      modeTitle: s.approved_for_live ? 'Approved for live trading' : 'Paper and backtest only',
      name: s.name, code: s.code,
      product: s.language === 'PYTHON' ? 'PY' : 'FORGE',
      detail: s.language === 'PYTHON' ? 'Python lane' : 'Rule graph',
      version: s.current_version == null ? '—' : `v${s.current_version}`,
      status: s.status, when: s.created_at, page: 'forge',
    }))
    const theirs: Row[] = (subs ?? []).map((s) => ({
      key: `a-${s.id}`, kind: 'atlas',
      mode: (s.last_deployed_mode as TradingMode | null) ?? 'BACKTEST',
      modeTitle: s.last_deployed_mode ? `Last deployed ${s.last_deployed_mode}` : 'Never deployed — published record is simulated',
      name: s.title, code: s.slug,
      product: s.tier, detail: `${s.risk_grade} · ${s.tier === 'OPEN' ? 'rules published' : 'sealed model'}`,
      version: `v${s.strategy_version}`,
      status: s.status === 'ACTIVE' ? (s.run_status ?? 'STOPPED') : 'CANCELLED',
      when: s.subscribed_at, page: 'atlas',
    }))
    const q = query.trim().toLowerCase()
    return [...theirs, ...mine]
      .filter((r) => kind === 'all' || r.kind === kind)
      .filter((r) => !onlyRunning || r.status === 'RUNNING')
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q))
  }, [strategies, subs, query, kind, onlyRunning])

  const activeSubs = (subs ?? []).filter((s) => s.status === 'ACTIVE').length
  const running = rows.filter((r) => r.status === 'RUNNING').length
  const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0)
  const todaysTrades = executions.filter((e) => Date.parse(e.exec_time) >= dayStart.getTime())
  const todaysVolume = todaysTrades.reduce((a, e) => a + Math.abs(Number(e.gross_value) || 0), 0)
  const todaysPnl = positions.reduce((a, p) => a + (Number(p.total_pnl) || 0), 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div className="qx-tiles">
        <Tile tone="green" icon="store" value={subs ? String(activeSubs) : '—'}
              label={`${PRODUCTS.marketplace.name} Strategies`}
              info="Active marketplace subscriptions" onClick={() => onNavigate('atlas')} />
        <Tile tone="pink" icon="blocks" value={strategies ? String(strategies.length) : '—'}
              label={`${PRODUCTS.studio.name} Strategies`}
              info="Strategies you own, any lane" onClick={() => onNavigate('forge')} />
        <Tile tone="orange" icon="play" value={String(running)} label="Running Strategies"
              info="Strategies with a run in RUNNING state" />
        <Tile tone="teal" icon="trend" value={fmtSigned(todaysPnl)} label="Today's P&L"
              info="Realised plus unrealised P&L across open positions" onClick={() => onNavigate('reports')} />
        <Tile tone="cyan" icon="coins" value={fmtMoney(todaysVolume, 'INR', true)} label="Today's Volume"
              info="Gross traded value of today's executions" onClick={() => onNavigate('trade')} />
      </div>

      <div className="qx-toolbar">
        <div className="qx-search" style={{ width: 380, maxWidth: '100%' }}>
          <input className="qx-input" placeholder="Search for Strategy" value={query}
                 onChange={(e) => setQuery(e.target.value)} aria-label="Search for strategy" />
          <Icon name="search" size={16} />
        </div>
        <button className={`qx-btn qx-btn-ghost${showFilters ? ' on' : ''}`} onClick={() => setShowFilters((v) => !v)}
                aria-expanded={showFilters}>
          <Icon name="filter" size={15} /> Filters
        </button>
        {showFilters && (
          <span className="qx-chips">
            {(['all', 'atlas', 'forge'] as const).map((k) => (
              <button key={k} className={`qx-chip${kind === k ? ' on' : ''}`} onClick={() => setKind(k)}>
                {k === 'all' ? 'All' : k === 'atlas' ? PRODUCTS.marketplace.name : PRODUCTS.studio.name}
              </button>
            ))}
            <button className={`qx-chip${onlyRunning ? ' on' : ''}`} onClick={() => setOnlyRunning((v) => !v)}>
              Running only
            </button>
          </span>
        )}
        <span className="spacer" />
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          {refreshedAt ? `Updated ${Math.max(0, Math.round((Date.now() - refreshedAt) / 1000))} seconds ago` : 'Loading…'}
        </span>
        <button className="qx-btn-icon" title="Refresh" onClick={() => void load()}><Icon name="refresh" size={17} /></button>
        <button className="qx-btn-icon" title="Settings" onClick={() => onNavigate('settings')}><Icon name="settings" size={17} /></button>
        <button className="qx-btn-icon" title="Open Live Reports" onClick={() => onNavigate('reports')}><Icon name="expand" size={17} /></button>
      </div>

      <Card noPadding>
        <div className="scroll-x">
          <table className="qx" style={{ fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr>
                <th style={{ width: 56 }}>Mode</th>
                <th>Strategy</th>
                <th>Instrument(s)</th>
                <th>Version</th>
                <th>Config</th>
                <th style={{ minWidth: 220 }}>Progress</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="qx-empty">
                  {strategies === null || subs === null
                    ? 'Loading…'
                    : `No strategies match. Subscribe in ${PRODUCTS.marketplace.name} or build one in ${PRODUCTS.studio.name}.`}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.key}>
                  <td><ModeDot mode={r.mode} title={r.modeTitle} /></td>
                  <td style={{ whiteSpace: 'normal', minWidth: 220 }}>
                    <button className="qx-link" onClick={() => onNavigate(r.page)}
                            style={{ textAlign: 'left', fontSize: 'var(--fs-md)' }}>
                      {r.name}
                    </button>
                    <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>{r.code}</div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <ProductPill label={r.product} />
                      <span>
                        <b>{r.kind === 'atlas' ? PRODUCTS.marketplace.name : PRODUCTS.studio.name}</b>
                        <span style={{ display: 'block', fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>{r.detail}</span>
                      </span>
                    </span>
                  </td>
                  <td className="num">{r.version}</td>
                  <td>
                    <button className="qx-btn-icon" title="Configure" onClick={() => onNavigate(r.page)}>
                      <Icon name="settings" size={17} />
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 'var(--fs-xs)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {r.status === 'RUNNING' ? 'Running' : r.status === 'CANCELLED' ? 'Subscription cancelled' : 'Not started yet'}
                      </span>
                      <span className={`qx-status ${r.status}`}>{r.status}</span>
                    </div>
                    <div className={`qx-progress${r.status === 'FAILED' || r.status === 'QUARANTINED' ? ' bad' : ''}`}>
                      <span style={{ width: r.status === 'RUNNING' ? '100%' : '0%' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 3 }}>
                      <span className="num">{fmtDateTime(r.when)}</span>
                      <button className="qx-link qx-link-sm" onClick={() => onNavigate(r.page)}>View Details</button>
                    </div>
                  </td>
                  <td>
                    <button className="qx-btn qx-btn-primary" onClick={() => onNavigate(r.page)}
                            title={r.status === 'RUNNING' ? 'Open to stop or inspect' : 'Open to deploy with risk checks'}>
                      {r.status === 'RUNNING' ? 'Open' : 'Start'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Portfolio />
    </div>
  )
}
